package com.eladfinish.bible.contest.features.copyOptions

import com.eladfinish.bible.contest.arch.ViewModelStore
import com.eladfinish.bible.contest.common.settings.KeyValueSettings
import com.eladfinish.bible.contest.data.common.DocumentTextType
import com.eladfinish.bible.contest.data.common.MyClipboardManager
import com.eladfinish.bible.contest.features.copyOptions.model.CopyOptionsConfig
import com.eladfinish.bible.contest.features.copyOptions.model.CopyOptionsInput
import com.eladfinish.bible.contest.features.copyOptions.model.CopyOptionsKeys
import com.eladfinish.bible.contest.features.copyOptions.model.CopyOptionsOperation
import com.eladfinish.bible.contest.platform.dispatchers.IoDispatcher
import com.eladfinish.bible.contest.screens.search.ui.HighlightType
import com.eladfinish.bible.contest.screens.search.usecases.copy.PrepareTextToCopyUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.withContext

class CopyOptionsStore(
    private val settings: KeyValueSettings,
    private val prepareTextToCopyUseCase: PrepareTextToCopyUseCase,
    private val myClipboardManager: MyClipboardManager,
) : ViewModelStore<CopyOptionsState, CopyOptionsAction, CopyOptionsEffect>(
    tag = "CopyOptionsStore",
) {

    override val stateFlow: MutableStateFlow<CopyOptionsState> = MutableStateFlow(
        value = CopyOptionsState(
            config = CopyOptionsConfig(
                shouldShowSearchedExpressionOption = false,
                shouldShowRemoveDuplicatesOption = false,
                shouldShowSortResultOption = false,
                shouldShowIncludeBookAndChapterOption = false,
                shouldShowHighlightsSection = false,
                shouldShowAdvancedOptionsSection = false,
            ),
            options = loadOptionsFromSettings(),
            operation = CopyOptionsOperation.COPY_TEXT,
            input = CopyOptionsInput.PlainText(
                rawText = "",
            ),
            bookAndChapterForExport = "",
            shouldAddParenthesisOnOtiotPasuk = false,
            documentTextType = DocumentTextType.TXT,
            previewText = "",
            isLoading = false,
        ),
    )

    override fun handleAction(
        action: CopyOptionsAction,
    ): CopyOptionsState =
        when (action) {
            is CopyOptionsAction.Init -> {
                handleInit(
                    config = action.config,
                    operation = action.operation,
                    input = action.input,
                    bookAndChapterForExport = action.bookAndChapterForExport,
                    shouldAddParenthesisOnOtiotPasuk = action.shouldAddParenthesisOnOtiotPasuk,
                    documentTextType = action.documentTextType,
                )
            }

            is CopyOptionsAction.SetIncludeExpression -> {
                handleSetIncludeExpression(
                    value = action.value,
                )
            }

            is CopyOptionsAction.SetIncludeNikud -> {
                handleSetIncludeNikud(
                    value = action.value,
                )
            }

            is CopyOptionsAction.SetIncludeTeamim -> {
                handleSetIncludeTeamim(
                    value = action.value,
                )
            }

            is CopyOptionsAction.SetHandleGodNames -> {
                handleSetHandleGodNames(
                    value = action.value,
                )
            }

            is CopyOptionsAction.SetIncludeBookAndChapter -> {
                handleSetIncludeBookAndChapter(
                    value = action.value,
                )
            }

            is CopyOptionsAction.SetHighlightType -> {
                handleSetHighlightType(
                    value = action.value,
                )
            }

            is CopyOptionsAction.SetRemoveVerseOrigin -> {
                handleSetRemoveVerseOrigin(
                    value = action.value,
                )
            }

            is CopyOptionsAction.SetCopyOnlyHighlights -> {
                handleSetCopyOnlyHighlights(
                    value = action.value,
                )
            }

            is CopyOptionsAction.SetRemoveDuplicates -> {
                handleSetRemoveDuplicates(
                    value = action.value,
                )
            }

            is CopyOptionsAction.SetSortResults -> {
                handleSetSortResults(
                    value = action.value,
                )
            }

            CopyOptionsAction.ConfirmCopyClicked -> {
                handleConfirmCopyClicked()
            }
        }

    // ---------------------------------------------------------
    // Handlers
    // ---------------------------------------------------------

    private fun handleInit(
        config: CopyOptionsConfig,
        operation: CopyOptionsOperation,
        input: CopyOptionsInput,
        bookAndChapterForExport: String,
        shouldAddParenthesisOnOtiotPasuk: Boolean,
        documentTextType: DocumentTextType,
    ): CopyOptionsState {
        val loadedOptions = loadOptionsFromSettings()

        val normalizedOptions = loadedOptions.normalizeForConfig(
            config = config,
        )

        val newState = currentState.copy(
            config = config,
            operation = operation,
            input = input,
            options = normalizedOptions,
            bookAndChapterForExport = bookAndChapterForExport,
            shouldAddParenthesisOnOtiotPasuk = shouldAddParenthesisOnOtiotPasuk,
            documentTextType = documentTextType,
        )

        return requestPreviewRecalculation(
            state = newState,
        )
    }

    private fun handleSetIncludeExpression(
        value: Boolean,
    ): CopyOptionsState =
        updateBooleanOption(
            key = CopyOptionsKeys.INCLUDE_EXPRESSION,
            value = value,
        ) { it.copy(includeExpression = value) }

    private fun handleSetIncludeNikud(
        value: Boolean,
    ): CopyOptionsState =
        updateBooleanOption(
            key = CopyOptionsKeys.INCLUDE_NIKUD,
            value = value,
        ) { it.copy(withNikud = value) }

    private fun handleSetIncludeTeamim(
        value: Boolean,
    ): CopyOptionsState =
        updateBooleanOption(
            key = CopyOptionsKeys.INCLUDE_TEAMIM,
            value = value,
        ) { it.copy(withTeamim = value) }

    private fun handleSetHandleGodNames(
        value: Boolean,
    ): CopyOptionsState =
        updateBooleanOption(
            key = CopyOptionsKeys.HANDLE_GOD_NAMES,
            value = value,
        ) { it.copy(hideGodNames = value) }

    private fun handleSetIncludeBookAndChapter(
        value: Boolean,
    ): CopyOptionsState =
        updateBooleanOption(
            key = CopyOptionsKeys.INCLUDE_BOOK_AND_CHAPTER,
            value = value,
        ) { it.copy(includeBookAndChapter = value) }

    private fun handleSetRemoveVerseOrigin(
        value: Boolean,
    ): CopyOptionsState =
        updateBooleanOption(
            key = CopyOptionsKeys.REMOVE_VERSE_ORIGIN,
            value = value,
        ) { it.copy(removeVerseOrigin = value) }

    private fun handleSetCopyOnlyHighlights(
        value: Boolean,
    ): CopyOptionsState =
        updateBooleanOption(
            key = CopyOptionsKeys.COPY_ONLY_HIGHLIGHTS,
            value = value,
        ) { it.copy(copyOnlyHighlights = value) }

    private fun handleSetRemoveDuplicates(
        value: Boolean,
    ): CopyOptionsState =
        updateBooleanOption(
            key = CopyOptionsKeys.REMOVE_DUPLICATES,
            value = value,
        ) { it.copy(removeDuplicates = value) }

    private fun handleSetSortResults(
        value: Boolean,
    ): CopyOptionsState =
        updateBooleanOption(
            key = CopyOptionsKeys.SORT_RESULTS,
            value = value,
        ) { it.copy(sortResults = value) }

    private fun handleSetHighlightType(
        value: HighlightType,
    ): CopyOptionsState {
        settings.putInt(
            key = CopyOptionsKeys.HIGHLIGHT_STYLE,
            value = value.ordinal,
        )

        val newOptions = currentState.options.copy(
            highlightType = value,
        ).normalizeForConfig(
            config = currentState.config,
        )

        val newState = currentState.copy(
            options = newOptions,
        )

        return requestPreviewRecalculation(
            state = newState,
        )
    }

    private fun handleConfirmCopyClicked(): CopyOptionsState {
        val text = currentState.previewText

        return when (currentState.operation) {
            CopyOptionsOperation.COPY_TEXT -> {
                myClipboardManager.copyToClipboard(
                    text = text,
                )
                onlyEffect(
                    CopyOptionsEffect.CloseDialog,
                )
            }

            CopyOptionsOperation.SAVE_FILE -> {
                launchEffect {
                    sideEffectFlow.emit(
                        CopyOptionsEffect.SaveFile(
                            content = text,
                        ),
                    )
                }
                currentState
            }
        }
    }

    // ---------------------------------------------------------
    // Helpers
    // ---------------------------------------------------------

    private fun updateBooleanOption(
        key: String,
        value: Boolean,
        reducer: (CopyOptions) -> CopyOptions,
    ): CopyOptionsState {
        settings.putBoolean(
            key = key,
            value = value,
        )

        val newOptions = reducer(
            currentState.options,
        ).normalizeForConfig(
            config = currentState.config,
        )

        val newState = currentState.copy(
            options = newOptions,
        )

        return requestPreviewRecalculation(
            state = newState,
        )
    }

    private fun requestPreviewRecalculation(
        state: CopyOptionsState,
    ): CopyOptionsState {
        launchEffect {
            val text = withContext(IoDispatcher) {
                buildPreviewText(
                    state = state,
                )
            }

            updateState { s ->
                s.copy(
                    previewText = text,
                    isLoading = false,
                )
            }
        }

        return state.copy(
            isLoading = true,
        )
    }

    private fun buildPreviewText(
        state: CopyOptionsState,
    ): String {
        val options = state.options

        return when (val input = state.input) {
            is CopyOptionsInput.PlainText -> {
                val body = prepareTextToCopyUseCase(
                    copyOptions = options,
                    rawText = input.rawText,
                )

                if (options.includeBookAndChapter) {
                    state.bookAndChapterForExport + body
                } else {
                    body
                }
            }

            is CopyOptionsInput.SingleSearchResult -> {
                val text = prepareTextToCopyUseCase(
                    copyOptions = options,
                    highlightType = options.highlightType,
                    item = input.item,
                )

                if (options.includeBookAndChapter) {
                    state.bookAndChapterForExport + text
                } else {
                    text
                }
            }

            is CopyOptionsInput.SearchResultsV2 -> {
                val text = prepareTextToCopyUseCase(
                    copyOptions = options,
                    highlightType = options.highlightType,
                    originalExpression = input.originalExpression,
                    items = input.items,
                )

                if (options.includeBookAndChapter) {
                    state.bookAndChapterForExport + text
                } else {
                    text
                }
            }
        }
    }

    private fun loadOptionsFromSettings(): CopyOptions {
        val highlightOrdinal = settings.getInt(
            key = CopyOptionsKeys.HIGHLIGHT_STYLE,
            defaultValue = HighlightType.BRACKETS.ordinal,
        )

        val highlight = HighlightType.entries.getOrElse(highlightOrdinal) {
            HighlightType.BRACKETS
        }

        return CopyOptions(
            withNikud = settings.getBoolean(
                key = CopyOptionsKeys.INCLUDE_NIKUD,
                defaultValue = true,
            ),
            withTeamim = settings.getBoolean(
                key = CopyOptionsKeys.INCLUDE_TEAMIM,
                defaultValue = true,
            ),
            hideGodNames = settings.getBoolean(
                key = CopyOptionsKeys.HANDLE_GOD_NAMES,
                defaultValue = true,
            ),
            includeBookAndChapter = settings.getBoolean(
                key = CopyOptionsKeys.INCLUDE_BOOK_AND_CHAPTER,
                defaultValue = true,
            ),
            sortResults = settings.getBoolean(
                key = CopyOptionsKeys.SORT_RESULTS,
                defaultValue = false,
            ),
            removeVerseOrigin = settings.getBoolean(
                key = CopyOptionsKeys.REMOVE_VERSE_ORIGIN,
                defaultValue = false,
            ),
            removeDuplicates = settings.getBoolean(
                key = CopyOptionsKeys.REMOVE_DUPLICATES,
                defaultValue = false,
            ),
            includeExpression = settings.getBoolean(
                key = CopyOptionsKeys.INCLUDE_EXPRESSION,
                defaultValue = true,
            ),
            copyOnlyHighlights = settings.getBoolean(
                key = CopyOptionsKeys.COPY_ONLY_HIGHLIGHTS,
                defaultValue = false,
            ),
            highlightType = highlight,
        )
    }

    private fun CopyOptions.normalizeForConfig(
        config: CopyOptionsConfig,
    ): CopyOptions {
        val normalizedIncludeBookAndChapter = if (config.shouldShowIncludeBookAndChapterOption) {
            includeBookAndChapter
        } else {
            false
        }

        val normalizedIncludeExpression = if (config.shouldShowSearchedExpressionOption) {
            includeExpression
        } else {
            false
        }

        val normalizedRemoveDuplicates = if (config.shouldShowRemoveDuplicatesOption) {
            removeDuplicates
        } else {
            false
        }

        val normalizedSortResults = if (config.shouldShowSortResultOption) {
            sortResults
        } else {
            false
        }

        val normalizedHighlightType = if (config.shouldShowHighlightsSection) {
            highlightType
        } else {
            HighlightType.NONE
        }

        return copy(
            includeBookAndChapter = normalizedIncludeBookAndChapter,
            includeExpression = normalizedIncludeExpression,
            removeDuplicates = normalizedRemoveDuplicates,
            sortResults = normalizedSortResults,
            highlightType = normalizedHighlightType,
        )
    }
}
